import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { QRCodeCanvas } from "qrcode.react";
import PGService from "../../services/PGService";

Modal.setAppElement("#root");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: "30px",
    maxWidth: "500px",
    width: "100%",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
};

const AddMoneyModal = ({
  isOpen,
  onRequestClose,
  balanceData,
  fetchBalance,
}) => {
  const [activeTab, setActiveTab] = useState("upi"); // "upi" or "hdfc"
  const [amount, setAmount] = useState("");
  const [orderId, setOrderId] = useState("");
  const [upiUrl, setUpiUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  // Reset modal state when closed or tab changes
  const resetModalState = () => {
    setAmount("");
    setUpiUrl("");
    setOrderId("");
    setTimeLeft(180);
    clearInterval(timerRef.current);
    clearInterval(intervalRef.current);
    setLoading(false);
  };

  // UPI QR generation (existing flow)
  const handleAddMoney = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      return Swal.fire("Oops!", "Please enter a valid amount", "error");
    }

    setLoading(true);
    try {
      const result = await PGService.getUPIQR(amount);

      if (!result.success) {
        return Swal.fire("Oops!", result.message || "Failed to fetch", "error");
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        setUpiUrl(data.upi_intent);
        setOrderId(data.order_id);
        setTimeLeft(180);

        timerRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              clearInterval(intervalRef.current);
              setUpiUrl("");
              setOrderId("");
              Swal.fire(
                "QR Expired",
                "Please try again to get a new QR code",
                "info",
              );
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || "Request failed",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // HDFC SmartHub PG order creation (redirects to gateway)
  const hdfcBankCreateOrder = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      return Swal.fire("Oops!", "Please enter a valid amount", "error");
    }

    setLoading(true);
    try {
      const result = await PGService.hdfcBankCreateOrder(amount);

      if (!result.success) {
        return Swal.fire("Oops!", result.message || "Failed to fetch", "error");
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        // Redirect to HDFC payment page
        window.location.href = data.redirect_url;
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || "Request failed",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Poll transaction status for UPI QR flow
  const txnStatus = async () => {
    if (!orderId) return;
    try {
      const result = await PGService.txnStatus(orderId);
      const responseData = result.data;

      if (result.success && responseData.status === "SUCCESS") {
        await fetchBalance();
        Swal.fire(
          "Success!",
          responseData.message || "Money added successfully",
          "success",
        );
        closeHandler();
      } else if (responseData.message === "FAILED") {
        Swal.fire(
          "Transaction Failed!",
          responseData?.data?.remark || "Transaction failed at the bank end",
          "error",
        );
        closeHandler();
      }
    } catch (error) {
      console.error("Transaction status check failed:", error);
    }
  };

  // Start polling for UPI flow when QR is shown
  useEffect(() => {
    if (activeTab === "upi" && upiUrl && orderId) {
      intervalRef.current = setInterval(() => {
        txnStatus();
      }, 15000);
    }

    return () => clearInterval(intervalRef.current);
  }, [activeTab, upiUrl, orderId]);

  // Cleanup timers when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
    return () => {
      resetModalState();
    };
  }, [isOpen]);

  // Reset state when switching tabs
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    resetModalState();
    setActiveTab(tab);
  };

  const closeHandler = () => {
    resetModalState();
    onRequestClose();
  };

  const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Render content based on active tab and UI state
  const renderTabContent = () => {
    if (activeTab === "upi") {
      if (!upiUrl) {
        // UPI: amount input + generate button
        return (
          <>
            <div className="mb-3">
              <label className="form-label">Enter Amount (₹)</label>
              <input
                type="number"
                className="form-control"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 500"
              />
            </div>
            <button
              className="btn btn-success w-100"
              onClick={handleAddMoney}
              disabled={loading}
            >
              {loading ? "Processing..." : "Generate QR to Pay"}
            </button>
          </>
        );
      } else {
        // UPI: QR code display
        return (
          <div className="text-center">
            <h6 className="fw-semibold mb-3">Scan this QR code to Add Money</h6>
            <QRCodeCanvas value={upiUrl} size={200} />
            <p className="mt-2 text-danger fw-bold">
              QR expires in: {formatTime(timeLeft)}
            </p>
            <p className="text-muted small">Scan & Pay with any BHIM UPI App</p>
          </div>
        );
      }
    } else {
      // HDFC SmartHub PG tab
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Enter Amount (₹)</label>
            <input
              type="number"
              className="form-control"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 500"
            />
          </div>
          <button
            className="btn btn-primary w-100"
            onClick={hdfcBankCreateOrder}
            disabled={loading}
          >
            {loading ? "Redirecting..." : "Pay via HDFC SmartHub"}
          </button>
        </>
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeHandler}
      style={customStyles}
      contentLabel="Add Money"
    >
      <div>
        <h4 className="mb-3 text-center text-primary fw-bold">
          Add Money to Wallet
        </h4>

        {/* Wallet Balance Card */}
        <div className="mb-4 d-flex gap-3">
          <div className="card flex-fill text-center shadow-sm border-0">
            <div className="card-body">
              <i className="bi bi-wallet2 fs-3 text-primary mb-2"></i>
              <h6 className="card-title mb-1">Wallet Balance</h6>
              <p className="card-text fw-bold" style={{ fontSize: "18px" }}>
                ₹{balanceData?.main_wallet || "0.00"}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "upi" ? "active" : ""}`}
              onClick={() => handleTabChange("upi")}
              style={{ cursor: "pointer" }}
            >
              UPI PG
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "hdfc" ? "active" : ""}`}
              onClick={() => handleTabChange("hdfc")}
              style={{ cursor: "pointer" }}
            >
              HDFC SmartHub PG
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        <div className="tab-content">{renderTabContent()}</div>

        <div className="text-end mt-4">
          <button className="btn btn-outline-secondary" onClick={closeHandler}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddMoneyModal;
